import { useEffect, useRef, useState } from 'react'

/** Movement smaller than this is ignored, so a jittery trackpad cannot flap the field. */
export const SEARCH_SCROLL_EPS = 6
/** Height of the field; above it there is nothing to gain by hiding it. */
export const SEARCH_COLLAPSE_FROM = 56

/**
 * Whether the search should be hidden after a scroll from `lastY` to `y`, or `null` when the move is too small
 * to decide (keep the current state, and keep measuring from `lastY`). Hidden on the way down past the field's
 * height, shown on the way up, and always shown near the top so it cannot get stranded.
 *
 * @example
 * searchHiddenAfterScroll(0, 120)  // true
 * searchHiddenAfterScroll(300, 290) // false
 */
export const searchHiddenAfterScroll = (lastY: number, y: number): boolean | null => {
  const dy = y - lastY
  if (Math.abs(dy) < SEARCH_SCROLL_EPS) return null
  return dy > 0 && y > SEARCH_COLLAPSE_FROM
}

/**
 * The mobile catalogue's search collapse: hides the field while the page scrolls down and brings it back on the
 * way up, so the category row rises to the top of the screen. Always shown when `enabled` is false.
 */
export function useSearchCollapseOnScroll(enabled: boolean): boolean {
  const [hidden, setHidden] = useState(false)
  const lastY = useRef(0)

  useEffect(() => {
    if (!enabled) {
      setHidden(false)
      return
    }
    lastY.current = window.scrollY
    const onScroll = () => {
      const next = searchHiddenAfterScroll(lastY.current, window.scrollY)
      if (next === null) return
      lastY.current = window.scrollY
      setHidden(next)
    }
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [enabled])

  return enabled && hidden
}
